import type { PlaybackLifecycleEvent } from "./lifecycle";
import type {
  DecoderEventDecision,
  PlayerCoordinatorCommand,
} from "./decoderEvents";

export type PlayerCommandAdapters = {
  dispatchLifecycle: (event: PlaybackLifecycleEvent) => void;
  clearRecoveryNotice: () => void;
  setRecoveryNotice: (message: string) => void;
  markSourcePlaying: () => void;
  sessionStart: () => void;
  sessionProgress: () => void;
  sessionStop: () => void;
  seek: (seconds: number, reportIntent: boolean) => void;
  seekDecoder: (seconds: number, reportIntent: boolean) => void;
  clearSeek: () => void;
  showControls: () => void;
  playNext: () => void;
  setServerSeek: (itemId?: string, seconds?: number) => void;
  beginSource: (input: {
    sourceId: string;
    recovering: boolean;
    positionSeconds: number;
    continuePausedSession: boolean;
  }) => void;
  retrySource: (positionSeconds: number, continuePausedSession: boolean) => void;
  resetTracks: () => void;
  closeMenu: () => void;
  refetchPlaybackInfo: () => void;
  showPlaybackPulse: (kind: "play" | "pause") => void;
};

function executeCommand(
  value: PlayerCoordinatorCommand,
  adapters: PlayerCommandAdapters,
) {
  switch (value.type) {
    case "CLEAR_RECOVERY_NOTICE":
      adapters.clearRecoveryNotice();
      return;
    case "SET_RECOVERY_NOTICE":
      adapters.setRecoveryNotice(value.message);
      return;
    case "MARK_SOURCE_PLAYING":
      adapters.markSourcePlaying();
      return;
    case "SESSION_START":
      adapters.sessionStart();
      return;
    case "SESSION_PROGRESS":
      adapters.sessionProgress();
      return;
    case "SESSION_STOP":
      adapters.sessionStop();
      return;
    case "SEEK":
      adapters.seek(value.seconds, value.reportIntent);
      return;
    case "SEEK_DECODER":
      adapters.seekDecoder(value.seconds, value.reportIntent);
      return;
    case "CLEAR_SEEK":
      adapters.clearSeek();
      return;
    case "SHOW_CONTROLS":
      adapters.showControls();
      return;
    case "PLAY_NEXT":
      adapters.playNext();
      return;
    case "SET_SERVER_SEEK":
      adapters.setServerSeek(value.itemId, value.seconds);
      return;
    case "BEGIN_SOURCE":
      adapters.beginSource(value);
      return;
    case "RETRY_SOURCE":
      adapters.retrySource(value.positionSeconds, value.continuePausedSession);
      return;
    case "RESET_TRACKS":
      adapters.resetTracks();
      return;
    case "CLOSE_MENU":
      adapters.closeMenu();
      return;
    case "REFETCH_PLAYBACK_INFO":
      adapters.refetchPlaybackInfo();
      return;
    case "SHOW_PLAYBACK_PULSE":
      adapters.showPlaybackPulse(value.kind);
      return;
  }
}

/** Execute a planner decision in its exact declared order. */
export function executePlayerDecision(
  decision: DecoderEventDecision,
  adapters: PlayerCommandAdapters,
) {
  for (const step of decision.steps) {
    if (step.type === "LIFECYCLE") adapters.dispatchLifecycle(step.event);
    else executeCommand(step.command, adapters);
  }
}
